/**
 * The firms a person may switch to, kept for this tab (OWA-1191). The ID token of a firm-scoped
 * sign-in names only the active firm, so the list the person chose from at sign-in is remembered
 * in the tab's own storage and read by the account menu.
 *
 * @module client/Auth/knownFirms
 */

import type {FirmChoice} from '@outlaw/signin';
import {tabSession} from './session';

const key = () => `${tabSession.namespace}:firms`;

/** Remember the firms a multi-firm sign-in named, for that person. A single firm leaves nothing
 * to switch to. */
export function rememberFirms(firms: FirmChoice[], subject: string): void {
    try {
        if (firms.length > 1 && subject) window.sessionStorage.setItem(key(), JSON.stringify({subject, firms}));
    } catch { /* The menu then offers no firm switch; signing in again restores it. */ }
}

/** The remembered firms of the person signed in to this tab, or none. A list remembered for
 * someone else (the tab's session ended some other way than Sign out) is ignored. */
export function knownFirms(subject: string | undefined): FirmChoice[] {
    try {
        const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key()) ?? 'null');
        if (!subject || !parsed || typeof parsed !== 'object') return [];
        const stored = parsed as {subject?: unknown; firms?: unknown};
        if (stored.subject !== subject || !Array.isArray(stored.firms)) return [];
        return stored.firms.filter((f): f is FirmChoice =>
            !!f && typeof f.firmOrgId === 'string' && typeof f.domain === 'string');
    } catch {
        return [];
    }
}

export function forgetFirms(): void {
    try { window.sessionStorage.removeItem(key()); } catch { /* Nothing stored. */ }
}
